/* Pick a box.

   The pop-up is opened by a small script written straight after it in the
   markup, before the page paints, so nothing of the page shows first and no
   fade stands between the two. This file does everything after that: the
   boxes, the gifts, the confetti and the claim. A visitor who has claimed
   in this tab's session is not shown it again; a new tab, or this one closed
   and opened again, is a new session and shows it once more.

   Elements are looked up by the nextdev--pick-a-box__* class names and the
   data attributes beside them. */

(function () {
  'use strict';

  // the one key the opener and this file share, in sessionStorage
  var KEY = 'nextdev-pick-a-box-claimed';

  function remember() {
    try {
      window.sessionStorage.setItem(KEY, '1');
    } catch (e) {
      // no storage: the pop-up simply shows again on the next page
    }
  }

  // the confetti is drawn in the brand's neutrals, read off the tokens so
  // no colour is written here by hand; orange is the call and never
  // decoration, so it is not among them
  function palette() {
    var root = getComputedStyle(document.documentElement);
    return ['bar-brown', 'earth', 'bone', 'ink'].map(function (name) {
      return root.getPropertyValue('--nextdev-color-' + name).trim();
    }).filter(Boolean);
  }

  function setup(dialog) {
    if (dialog.dataset.nextdevPickABoxInit) return;
    dialog.dataset.nextdevPickABoxInit = 'true';
    if (typeof dialog.showModal !== 'function') return;

    // in the theme editor the pop-up stands in the page, open and not
    // modal, so the merchant can see it and still reach the editor
    var editor = dialog.hasAttribute('data-nextdev-pick-a-box-editor');

    var grid = dialog.querySelector('[data-nextdev-pick-a-box-grid]');
    var prizes = Array.prototype.slice.call(dialog.querySelectorAll('[data-nextdev-pick-a-box-prize]'));
    var status = dialog.querySelector('[data-nextdev-pick-a-box-status]');
    var cta = dialog.querySelector('[data-nextdev-pick-a-box-cta]');
    var ctaLabel = dialog.querySelector('[data-nextdev-pick-a-box-cta-label]');
    var layer = dialog.querySelector('[data-nextdev-pick-a-box-fx]');
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var leftTemplate = dialog.getAttribute('data-left-label') || '[boxes] left to open';
    var claimLabel = dialog.getAttribute('data-claim-label') || 'Claim your free gifts';
    var COLORS = palette();

    // one box per gift is opened, between one and five
    var PICKS = prizes.length;
    if (!grid || !cta || !PICKS) return;

    var order = [];
    var opened = 0;
    var shakeTimer = null;
    var claimed = false;

    /* ---------------------------------------------------------------
       The cart drawer's celebration, in the brand's neutrals.
       --------------------------------------------------------------- */
    function confetti(x, y, opts) {
      if (reduced || !COLORS.length) return;
      opts = opts || {};
      var pieces = opts.pieces || 16;
      var reach = opts.reach || 1;
      var burst = document.createElement('div');
      burst.className = 'nextdev--pick-a-box__burst';
      burst.style.left = x + 'px';
      burst.style.top = y + 'px';

      for (var i = 0; i < pieces; i++) {
        var p = document.createElement('i');
        var spread = opts.full ? 360 : 130;
        var start = opts.full ? 0 : -155;
        var angle = (start + Math.random() * spread) * Math.PI / 180;
        var dist = (32 + Math.random() * 56) * reach;
        p.style.setProperty('--dx', Math.cos(angle) * dist * 0.8 + 'px');
        p.style.setProperty('--dy', Math.sin(angle) * dist + (opts.full ? 40 : 0) + 'px');
        p.style.setProperty('--rot', (Math.random() * 540 - 270) + 'deg');
        p.style.background = COLORS[i % COLORS.length];
        p.style.animationDelay = (Math.random() * 90) + 'ms';
        if (i % 3 === 0) p.style.borderRadius = '50%';
        if (i % 4 === 1) { p.style.width = '5px'; p.style.height = '11px'; }
        burst.appendChild(p);
      }

      layer.appendChild(burst);
      setTimeout(function () { burst.remove(); }, 1300);
    }

    function bubble(x, y, text) {
      if (reduced || !text) return;
      var anchor = document.createElement('div');
      anchor.className = 'nextdev--pick-a-box__bubble-anchor';
      anchor.style.left = x + 'px';
      anchor.style.top = y + 'px';
      var b = document.createElement('div');
      b.className = 'nextdev--pick-a-box__bubble';
      b.textContent = text;
      anchor.appendChild(b);
      layer.appendChild(anchor);
      // kept 8 in from either edge, so a box at the side of a phone does
      // not push its line off the screen
      var half = b.offsetWidth / 2;
      var room = document.documentElement.clientWidth;
      anchor.style.left = Math.min(Math.max(x, half + 8), room - half - 8) + 'px';
      setTimeout(function () { anchor.remove(); }, 2300);
    }

    /* the finale: bursts across the card, one after another */
    function finale() {
      if (reduced) return;
      var r = dialog.getBoundingClientRect();
      var points = [[0.2, 0.35], [0.8, 0.35], [0.5, 0.55], [0.3, 0.75], [0.7, 0.75]];
      points.forEach(function (pt, i) {
        setTimeout(function () {
          confetti(r.left + r.width * pt[0], r.top + r.height * pt[1], { pieces: 26, reach: 1.9, full: true });
        }, i * 140);
      });
    }

    /* ---------------------------------------------------------------
       The boxes
       --------------------------------------------------------------- */
    function shuffle(list) {
      var a = list.slice();
      for (var i = a.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var t = a[i]; a[i] = a[j]; a[j] = t;
      }
      return a;
    }

    function boxes() {
      return Array.prototype.slice.call(grid.querySelectorAll('[data-nextdev-pick-a-box-box]'));
    }

    // every box back to closed; the markup draws them closed already, so
    // on the first run this changes nothing a visitor could see
    function close(btn, i) {
      btn.classList.remove(
        'nextdev--pick-a-box__box--open',
        'nextdev--pick-a-box__box--spent',
        'nextdev--pick-a-box__box--shake'
      );
      btn.removeAttribute('aria-disabled');
      btn.removeAttribute('data-opened');
      btn.setAttribute('aria-label', 'Box ' + (i + 1) + ', closed');
      var found = btn.querySelector('.nextdev--pick-a-box__found');
      if (found) found.remove();
    }

    // how far along the waiting call is: a fill behind its label, one share
    // per box opened, which the css draws from --nextdev-pick-a-box-progress
    function progress() {
      cta.style.setProperty('--nextdev-pick-a-box-progress', (opened / PICKS * 100) + '%');
    }

    // what the call says while it waits: how many boxes are still to open
    function left(n) {
      return leftTemplate.replace('[boxes]', n + (n === 1 ? ' box' : ' boxes'));
    }

    // the gifts come out shuffled, or in the order of the blocks when the
    // section says so
    var asPlaced = dialog.getAttribute('data-gift-order') === 'blocks';

    function reset() {
      order = asPlaced ? prizes.slice() : shuffle(prizes);
      opened = 0;
      claimed = false;
      // the row is dealt in the order the gifts will come out, so they
      // unlock left to right; a locked tile looks like every other one, so
      // dealing them after the first paint shows nothing moving
      order.forEach(function (p) {
        p.classList.remove('nextdev--pick-a-box__prize--found');
        p.parentNode.appendChild(p);
      });
      boxes().forEach(close);
      layer.innerHTML = '';
      cta.classList.remove('nextdev--pick-a-box__cta--ready');
      cta.setAttribute('aria-disabled', 'true');
      ctaLabel.textContent = left(PICKS);
      progress();
      status.textContent = '';
    }

    // the gift's own picture in a bone disc, the one that rises out of the box
    function foundFor(prize) {
      var el = document.createElement('span');
      el.className = 'nextdev--pick-a-box__found nextdev--pick-a-box__found--image';
      var media = prize.querySelector('.nextdev--pick-a-box__prize-media');
      if (media) {
        var copy = media.cloneNode(true);
        copy.removeAttribute('srcset');
        copy.removeAttribute('sizes');
        copy.removeAttribute('loading');
        if (media.currentSrc) copy.src = media.currentSrc;
        el.appendChild(copy);
      }
      return el;
    }

    function nameOf(prize) {
      var name = prize.querySelector('.nextdev--pick-a-box__prize-name');
      return name ? name.textContent.trim() : '';
    }

    function open(btn) {
      if (btn.getAttribute('aria-disabled') === 'true' || opened >= PICKS) return;
      var prize = order[opened];
      opened += 1;
      progress();

      btn.insertBefore(foundFor(prize), btn.firstChild);
      btn.classList.remove('nextdev--pick-a-box__box--shake');
      // a frame between inserting and opening, so the gift rises rather than appearing
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          btn.classList.add('nextdev--pick-a-box__box--open');
        });
      });
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('data-opened', '');

      var name = nameOf(prize);
      btn.setAttribute('aria-label', 'Box, opened: ' + name);
      var last = opened === PICKS;
      // once the gift has risen out of its box it flies up to its tile,
      // and the tile unlocks where it lands
      setTimeout(function () {
        fly(btn.querySelector('.nextdev--pick-a-box__found'), prize, function () {
          prize.classList.add('nextdev--pick-a-box__prize--found');
          var t = prize.querySelector('.nextdev--pick-a-box__prize-tile').getBoundingClientRect();
          confetti(t.left + t.width / 2, t.top + t.height / 2, { pieces: 12, reach: 0.8 });
          if (last) ready();
        });
      }, reduced ? 0 : 700);

      var r = btn.getBoundingClientRect();
      var cx = r.left + r.width / 2;
      var cy = r.top + r.height * 0.3;
      setTimeout(function () {
        confetti(cx, cy, { pieces: 18, reach: 1.2 });
        bubble(cx, r.top + 4, name);
      }, reduced ? 0 : 220);

      if (!last) {
        ctaLabel.textContent = left(PICKS - opened);
        status.textContent = 'You found: ' + name + '. ' + ctaLabel.textContent + '.';
        return;
      }

      status.textContent = 'You found: ' + name + '. That is every gift.';
      boxes().forEach(function (b) {
        if (!b.hasAttribute('data-opened')) {
          b.setAttribute('aria-disabled', 'true');
          b.classList.add('nextdev--pick-a-box__box--spent');
        }
      });
    }

    // the call turns orange once the last gift has landed, not before
    function ready() {
      cta.classList.add('nextdev--pick-a-box__cta--ready');
      cta.removeAttribute('aria-disabled');
      ctaLabel.textContent = claimLabel;
      setTimeout(finale, reduced ? 0 : 250);
    }

    /* A copy of the disc flies from the box to the middle of its tile on a
       short arc, shrinking to the size of the picture there, then fades as
       the lock opens under it. The disc stays in its box, so an open box
       keeps showing what it gave rather than standing empty. */
    function fly(disc, prize, land) {
      var tile = prize.querySelector('.nextdev--pick-a-box__prize-tile');
      if (reduced || !disc || !tile || typeof disc.animate !== 'function') {
        land();
        return;
      }
      var from = disc.getBoundingClientRect();
      var to = tile.getBoundingClientRect();
      var copy = disc.cloneNode(true);
      copy.classList.add('nextdev--pick-a-box__flyer');
      copy.style.left = from.left + 'px';
      copy.style.top = from.top + 'px';
      copy.style.width = from.width + 'px';
      copy.style.height = from.height + 'px';
      layer.appendChild(copy);

      var dx = (to.left + to.width / 2) - (from.left + from.width / 2);
      var dy = (to.top + to.height / 2) - (from.top + from.height / 2);
      var scale = (to.width * 0.8) / from.width;
      var lift = Math.min(80, Math.abs(dy) * 0.25);
      var flight = copy.animate([
        { transform: 'translate(0, 0) scale(1)' },
        { transform: 'translate(' + dx * 0.45 + 'px, ' + (dy * 0.45 - lift) + 'px) scale(' + (1 + scale) / 2 * 1.08 + ')', offset: 0.45 },
        { transform: 'translate(' + dx + 'px, ' + dy + 'px) scale(' + scale + ')' }
      ], { duration: 650, easing: 'cubic-bezier(0.45, 0, 0.25, 1)', fill: 'forwards' });

      flight.onfinish = function () {
        land();
        var fade = copy.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 450, delay: 150, fill: 'forwards' });
        fade.onfinish = function () { copy.remove(); };
      };
    }

    function shake(b) {
      if (!b || reduced) return;
      b.classList.remove('nextdev--pick-a-box__box--shake');
      void b.offsetWidth;
      b.classList.add('nextdev--pick-a-box__box--shake');
    }

    /* a closed box nudges now and then, so the grid reads as something to press */
    function nudge() {
      clearInterval(shakeTimer);
      if (reduced) return;
      shakeTimer = setInterval(function () {
        if (!dialog.open || opened >= PICKS) return;
        var closed = grid.querySelectorAll('[data-nextdev-pick-a-box-box]:not([aria-disabled="true"])');
        if (!closed.length) return;
        shake(closed[Math.floor(Math.random() * closed.length)]);
      }, 2400);
    }

    grid.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-nextdev-pick-a-box-box]');
      if (btn) open(btn);
    });
    grid.addEventListener('animationend', function (event) {
      if (event.animationName === 'nextdev--pick-a-box-shake') {
        event.target.classList.remove('nextdev--pick-a-box__box--shake');
      }
    });

    cta.addEventListener('click', function () {
      if (cta.getAttribute('aria-disabled') === 'true') {
        // not ready yet: shake a closed box rather than doing nothing
        shake(grid.querySelector('[data-nextdev-pick-a-box-box]:not([aria-disabled="true"])'));
        return;
      }
      // The claim lets the visitor through to the page and nothing more:
      // the gifts are the shop's to give, on the owner's call. In the
      // editor the pop-up starts over instead, so it can be tried again.
      if (editor) {
        reset();
        return;
      }
      if (claimed) return;
      claimed = true;
      remember();
      leave();
    });

    // The cross in the corner and the line under the call close it straight
    // away, with nothing to open first. They go the way the claim goes, so
    // it stays closed for the rest of the session; in the editor they start
    // it over, as the claim does there.
    Array.prototype.forEach.call(dialog.querySelectorAll('[data-nextdev-pick-a-box-dismiss]'), function (btn) {
      btn.addEventListener('click', function () {
        if (editor) {
          reset();
          return;
        }
        if (claimed) return;
        claimed = true;
        remember();
        leave();
      });
    });

    // On the claim the pop-up fades out over the page, on the owner's call,
    // and only then closes; under reduced motion it closes at once. A
    // second press while it fades does nothing, since it is already claimed.
    function leave() {
      if (reduced || typeof dialog.animate !== 'function') {
        dialog.close();
        return;
      }
      dialog.classList.add('nextdev--pick-a-box--leaving');
      var fade = dialog.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 450, easing: 'ease', fill: 'forwards' });
      var done = false;
      var finish = function () {
        if (done) return;
        done = true;
        dialog.close();
        fade.cancel();
        dialog.classList.remove('nextdev--pick-a-box--leaving');
      };
      fade.onfinish = finish;
      // a tab in the background holds its animations, so the finish may
      // never come while it is hidden: a timer closes it either way
      setTimeout(finish, 600);
    }

    if (editor) {
      reset();
      nudge();
      return;
    }

    // The only way through is the claim. closedby="none" on the dialog
    // tells a browser that knows it that nothing but the page may close
    // it; escape is refused besides, and where a browser closes it anyway
    // (Chrome lets a second escape through when there has been no click in
    // between) it opens again exactly as it was left.
    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
    });
    dialog.addEventListener('close', function () {
      if (!claimed) {
        dialog.showModal();
        dialog.focus({ preventScroll: true });
        return;
      }
      clearInterval(shakeTimer);
      layer.innerHTML = '';
    });

    // the opener has already shown it, or has not because it was claimed
    // earlier in this session; only an open pop-up has anything to deal
    if (!dialog.open) return;
    reset();
    nudge();
  }

  function start() {
    document.querySelectorAll('[data-nextdev-pick-a-box]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // the theme editor draws a section again after a change
  document.addEventListener('shopify:section:load', start);
})();
